import { useState, useEffect } from 'react';
import ContactLine from './components/ContactLine';
import AddContactModal from './components/AddContactModal';
import VerifyContactModal from './components/VerifyContactModal';
import UpdateContactModal from './components/UpdateContactModal';

function Contacts({ trads, user, authorizedCountries }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewContact, setShowNewContact] = useState(false);
  const [verifyingContact, setVerifyingContact] = useState(null);
  const [updatingContact, setUpdatingContact] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('all');

  const t = trads?.contacts || {};

  const fetchContacts = async () => {
    try {
      const res = await fetch('/api/contacts/get-contacts');
      const data = await res.json();
      if (data.success && data.contacts) {
        setContacts(data.contacts);
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const shouldOpenModal = searchParams.get('modal') === 'true';
    if (shouldOpenModal) {
      setShowNewContact(true);
    }
  }, []);

  useEffect(() => {
    if (
      selectedGroupId !== 'all' &&
      !groups.some((group) => String(group.id) === String(selectedGroupId))
    ) {
      setSelectedGroupId('all');
    }
  }, [groups, selectedGroupId]);

  const handleNameChange = async (contactId, newName) => {
    if (!newName.trim()) return;

    try {
      const contact = contacts.find((c) => c.id === contactId);
      const res = await fetch('/api/contacts/update-or-create-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: contactId,
          name: newName,
          phone: contact?.phone || '',
        }),
      });

      const data = await res.json();
      if (data.success && data.contact) {
        setContacts((prev) =>
          prev.map((c) => (c.id === data.contact.id ? data.contact : c))
        );
      } else {
        console.error('Failed to update contact:', data.error);
      }
    } catch (err) {
      console.error('Error updating contact:', err);
    }
  };

  const handleSendVerification = async (contactId) => {
    try {
      const res = await fetch('/api/contacts/send-verification-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId }),
      });

      const data = await res.json();
      if (data.success) {
        alert(t.verificationSent || 'Verification SMS sent!');
      } else {
        alert(data.error || 'Failed to send verification SMS');
      }
    } catch (err) {
      console.error('Error sending verification:', err);
      alert('Error sending verification SMS');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      const res = await fetch('/api/contacts/delete-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId }),
      });

      const data = await res.json();
      if (data.success) {
        fetchContacts();
      } else {
        alert(data.error || 'Failed to delete contact');
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Error deleting contact');
    }
  };

  const handleAddContact = (data) => {
    fetchContacts();
    if (data.verificationPending) {
      setVerifyingContact(data.contact);
    }
  };

  const handleVerified = (updatedContact) => {
    setContacts((prev) =>
      prev.map((c) => (c.id === updatedContact.id ? updatedContact : c))
    );
  };

  const filteredContacts =
    selectedGroupId === 'all'
      ? contacts
      : contacts.filter((contact) =>
          (contact.groupMemberships || []).some(
            (groupMembership) => groupMembership.id === Number(selectedGroupId)
          )
        );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        {t.loading || 'Loading...'}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        {t.pleaseLogin || 'Please log in to view your contacts.'}
      </div>
    );
  }

  return (
    <div className="">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">
        {t.title || 'Contacts'}
      </h1>

      <div className="pb-2 pt-2 pl-2">
        <button
          type="button"
          onClick={() => setShowNewContact(true)}
          data-testid="add-contact-btn"
          className="text-lg font-medium text-gray-900 underline hover:text-gray-700 cursor-pointer"
        >
          {t.addContact || 'Add contact'}
        </button>
      </div>

      <div className="pt-2 pl-2 mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t.filterByGroup || 'Filter by group'}
        </label>
        <select
          value={selectedGroupId}
          onChange={(event) => setSelectedGroupId(event.target.value)}
          data-testid="contacts-group-filter"
          className="w-full max-w-[300px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">{t.allGroups || 'All groups'}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-lg shadow">
        {contacts.length === 0 && !showNewContact ? (
          <div className="p-6 text-center text-gray-500">
            {t.noContacts || 'No contacts yet.'}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {filteredContacts.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">
                {t.noContactsInGroup || 'No contacts in this group.'}
              </div>
            ) : (
              filteredContacts.map((contact) => (
                <ContactLine
                  key={contact.id}
                  contact={contact}
                  trads={trads}
                  onNameChange={handleNameChange}
                  onSendVerification={handleSendVerification}
                  onDelete={handleDeleteContact}
                  onVerify={() => setVerifyingContact(contact)}
                  onUpdate={() => setUpdatingContact(contact)}
                />
              ))
            )}
          </div>
        )}
      </div>

      <AddContactModal
        isOpen={showNewContact}
        onClose={() => setShowNewContact(false)}
        onAdd={handleAddContact}
        trads={trads}
        authorizedCountries={authorizedCountries}
      />

      <VerifyContactModal
        isOpen={!!verifyingContact}
        contact={verifyingContact}
        onClose={() => setVerifyingContact(null)}
        onVerified={handleVerified}
        trads={trads}
      />

      <UpdateContactModal
        isOpen={!!updatingContact}
        contact={updatingContact}
        groups={groups}
        trads={trads}
        onClose={() => setUpdatingContact(null)}
        onUpdated={() => fetchContacts()}
      />
    </div>
  );
}

export default Contacts;
